import arrowDown from "/assets/arrow-down.svg";
import checkMark from "/assets/check-mark.svg";
import close from "/assets/close.svg";
import { useState, useEffect, useRef } from "react";
import classNames from "classnames";

export default function MultiSelect({
  label,
  placeholder,
  value = [],
  locale,
  options = [],
  onChange,
  disabled = false,
  errors = [],
}) {
  if (typeof onChange !== "function" && !disabled)
    console.debug("A multi-select doesn't have an onChange callback");

  const [isValid, setIsValid] = useState(errors.length == 0);
  const [isOptionsVisible, setIsOptionsVisible] = useState(false);
  const wrapperRef = useRef(null);
  const optionsRef = useRef(null);
  const labelRef = useRef(null);
  const normValue = value.map(v => v.toString());
  const optionLabel = (data) => (locale ? data[locale] : data);
  const sortedOptions = Object.entries(options)
    .sort(([, a], [, b]) => optionLabel(a).localeCompare(optionLabel(b)));

  useEffect(() => {
    setIsValid(errors.length == 0);
  }, [errors]);

  useEffect(() => {
    const handleOutsideClick = event => {
      if (!isOptionsVisible) return;

      // The box, the label and the list all toggle the dropdown themselves, so
      // a click on any of them must not also close it here. `?.` because the
      // label is only rendered when the `label` prop is given.
      const isInside = [wrapperRef, optionsRef, labelRef]
        .some(ref => ref.current?.contains(event.target));

      if (!isInside) setIsOptionsVisible(false);
    }

    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOptionsVisible]);

  const toggleOptionsVisible = () => {
    if (disabled) return;

    setIsOptionsVisible(currentValue => !currentValue);
  };

  const toggleOption = (targetOption) => {
    if (normValue.includes(targetOption)) {
      removeOption(targetOption);
    } else {
      addOption(targetOption);
    }
  };

  const addOption = (key) => {
    if (typeof onChange !== 'function') return;

    onChange([...value, key]);
    setIsValid(true);
  };

  const removeOption = (key) => {
    if (typeof onChange !== 'function') return;

    onChange(value.filter(o => o != key));
    setIsValid(true);
  };

  return (
    <div className="inpt-grp">
      {label && (
        <label onClick={toggleOptionsVisible} ref={labelRef} className="lbl">{label}</label>
      )}
      <div className="inpt-select-wrapper">
        <div className={classNames("inpt-select", { "inpt-select-disabled": disabled })} onClick={toggleOptionsVisible} ref={wrapperRef}>
          <span className="inpt-select-value">
            {value.length > 0 ? (
              <>
                {value.map(selectedKey => (
                  <div key={selectedKey} className="inpt-select-selected-value">
                    {optionLabel(options[selectedKey])}
                    {!disabled && (<img src={close} onClick={(event) => { event.stopPropagation(); removeOption(selectedKey) }} />)}
                  </div>
                ))}
              </>
            ) : placeholder}
          </span>

          {!disabled && (
            <img src={arrowDown} className={classNames("inpt-select-icon", { "inpt-select-icon-open": isOptionsVisible })} />
          )}
        </div>

        {!isValid && errors.map((error, index) => (
          <label key={index} className="lbl lbl--error">{error}</label>
        ))}

        {isOptionsVisible && (
          <ul className="inpt-select-options" ref={optionsRef}>
            {sortedOptions.map(([key, data]) => (
              <li
                key={key}
                className={classNames({ "selected": normValue.includes(key) })}
                onClick={() => toggleOption(key)}
              >
                <div className="inpt-select-checkbox">
                  <img src={checkMark} />
                </div>

                {optionLabel(data)}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
